// src/components/Config/ModalEdit.jsx
import React, { useState, useEffect, useContext, useCallback } from "react";
import {
	Modal,
	Box,
	Typography,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Alert,
	Button,
	CircularProgress,
} from "@mui/material";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import { GlobalContext } from "../../GlobalContext";
import { actualizarDato, auditPost, obtenerDatosSites } from "../../api/config";
import FormConfig from "./FormConfig";
import { GridEdit } from "./GridEdit";
import { validateField } from "../../Labels/descriptions/LabelsDescriptionsRestrictions/Restrictions";

export default function ModalEdit({ open, onClose, contractData, onCreated }) {
	const { t } = useTranslation();
	const { getModuleId, site_id } = useContext(GlobalContext);
	const moduleId = getModuleId("Configuración", "Editar Contrato", "Editar contrato");

	// — Paso actual y loading —
	const [paso, setPaso] = useState(1);
	const [loading, setLoading] = useState(true);

	// — Select tipo contrato —
	const [tipoContratos, setTipoContratos] = useState([]);
	const [tipoContrato, setTipoContrato] = useState("");
	const [errorSelect, setErrorSelect] = useState(false);

	// — Campos generales y validación —
	const [values, setValues] = useState({});
	const [errors, setErrors] = useState({});
	const [formError, setFormError] = useState("");

	// — Estado de calendario —
	const [schedule, setSchedule] = useState({ days: [], periods: [] });
	const handleScheduleChange = useCallback((days, periods) => {
		setSchedule({ days, periods });
		setFormError("");
	}, []);

	// — Configs de validación para FormConfig —
	const fieldConfigs = {
		[t("lab_tool.contract.labels.nombre")]: { minLength: 3, regex: /^[\p{L}\d ]+$/u },
		[t("lab_tool.contract.labels.kronos_name")]: { minLength: 3, regex: /^[\p{L}\d ]+$/u },
		[t("lab_tool.contract.labels.productividad")]: { type: "number", allowDecimal: true },
		[t("lab_tool.contract.labels.rotacion")]: { type: "number", allowDecimal: true },
		[t("lab_tool.contract.labels.zcero")]: { type: "number", allowDecimal: false },
		[t("lab_tool.contract.labels.cotsup")]: { type: "number", allowDecimal: true },
		[t("lab_tool.contract.labels.cotinf")]: { type: "number", allowDecimal: true },
		[t("lab_tool.contract.labels.ausentismo")]: { type: "number", allowDecimal: true },
		[t("lab_tool.contract.labels.contrato")]: { type: "number", allowDecimal: true },
		[t("lab_tool.contract.labels.despido")]: { type: "number", allowDecimal: true },
		[t("lab_tool.contract.labels.salario")]: { type: "number", allowDecimal: true },
		[t("lab_tool.contract.labels.hora_contrato")]: { type: "number", allowDecimal: true },
		[t("lab_tool.contract.labels.dias_lab_maximos")]: { type: "number", allowDecimal: false },
		[t("lab_tool.contract.labels.dias_lab_minimos")]: { type: "number", allowDecimal: false },
		[t("lab_tool.contract.labels.contrato_extra")]: { type: "checkbox" },
		[t("lab_tool.contract.labels.meses_contrato")]: {
			type: "text",
			allowDecimal: false,
			dependsOn: t("lab_tool.contract.labels.contrato_extra"),
			dependsValue: "true",
		},
	};
	const tooltips = [
		t("lab_tool.contract.tooltips.nombre"),
		t("lab_tool.contract.tooltips.kronos_name"),
		t("lab_tool.contract.tooltips.productividad"),
		t("lab_tool.contract.tooltips.rotacion"),
		t("lab_tool.contract.tooltips.ausentismo"),
		t("lab_tool.contract.tooltips.cotsup"),
		t("lab_tool.contract.tooltips.cotinf"),
		t("lab_tool.contract.tooltips.dias_lab_maximos"),
		t("lab_tool.contract.tooltips.dias_lab_minimos"),
		t("lab_tool.contract.tooltips.contrato"),
		t("lab_tool.contract.tooltips.despido"),
		t("lab_tool.contract.tooltips.salario"),
		t("lab_tool.contract.tooltips.zcero"),
		t("lab_tool.contract.tooltips.hora_contrato"),
		t("lab_tool.contract.tooltips.meses_contrato"),
		t("lab_tool.contract.tooltips.contrato_extra"),
	];

	// — renameData para inicializar formValues —
	const renameData = React.useMemo(() => {
		if (!contractData) return {};
		return {
			[t("lab_tool.contract.labels.nombre")]: contractData.nombre,
			[t("lab_tool.contract.labels.kronos_name")]: contractData.kronos_name,
			[t("lab_tool.contract.labels.productividad")]: contractData.productividad,
			[t("lab_tool.contract.labels.rotacion")]: contractData.rotacion,
			[t("lab_tool.contract.labels.ausentismo")]: contractData.ausentismo,
			[t("lab_tool.contract.labels.cotsup")]: contractData.cotsup,
			[t("lab_tool.contract.labels.cotinf")]: contractData.cotinf,
			[t("lab_tool.contract.labels.dias_lab_maximos")]: contractData.dias_lab_maximos,
			[t("lab_tool.contract.labels.dias_lab_minimos")]: contractData.dias_lab_minimos,
			[t("lab_tool.contract.labels.contrato")]: contractData.contrato,
			[t("lab_tool.contract.labels.despido")]: contractData.despido,
			[t("lab_tool.contract.labels.salario")]: contractData.salario,
			[t("lab_tool.contract.labels.zcero")]: contractData.zcero,
			[t("lab_tool.contract.labels.hora_contrato")]: contractData.hora_contrato,
			[t("lab_tool.contract.labels.contrato_extra")]: contractData.contrato_extra ? "true" : "false",
			[t("lab_tool.contract.labels.meses_contrato")]: contractData.contrato_extra
				? String(contractData.meses_contrato ?? "0")
				: "0",
		};
	}, [contractData, t]);

	// — Carga inicial cuando se abre o cambia el contrato —
	useEffect(() => {
		if (!open || !contractData) return;

		setLoading(true);
		setPaso(1);
		setValues(renameData);
		setErrors({});
		setFormError("");
		setTipoContrato(contractData.id_tipo_contrato?.toString() ?? "");
		setErrorSelect(false);

		// fetch tipos
		obtenerDatosSites(`/tipo_contrato/get?site_id=${site_id}`)
			.then((resp) => setTipoContratos(resp))
			.catch(console.error)
			.finally(() => setLoading(false));

		// init schedule
		const days = (contractData.contracts_per_day || []).map((d) => ({
			config_contracts_per_days_id: d.config_contracts_per_days_id,
			contract_id: contractData.id,
			day: d.day,
			value: d.value,
		}));
		const periods = (contractData.contracts_per_periods || []).map((p) => ({
			config_contracts_per_days_id: p.config_contracts_per_days_id,
			config_contracts_per_periods_id: p.config_contracts_per_periods_id,
			periods: p.periods.map(({ start_time, end_time }) => ({ start_time, end_time })),
		}));
		setSchedule({ days, periods });
	}, [open, contractData, renameData, site_id]);

	// — Cambios en campos generales —
	const handleFieldChange = (field, val) => {
		setValues((v) => {
			const upd = { ...v, [field]: val };
			// si desactivas extra, borras meses
			if (field === t("lab_tool.contract.labels.contrato_extra") && val === "false") {
				upd[t("lab_tool.contract.labels.meses_contrato")] = "0";
			}
			setErrors((e) => ({
				...e,
				[field]: validateField(field, val, upd, t),
			}));
			return upd;
		});
	};

	// — Siguiente paso: preparo dataDays/dataPeriods —
	const handleNextStep = () => {
		setPaso(2);
	};
	const handleBack = () => setPaso(1);

	// — Guardar: validaciones finales y API call —
	const handleSave = () => {
		setFormError("");
		// validaciones calendario
		const allEmpty = schedule.days.every((d) => d.value === 0);
		const incomplete = schedule.periods.some((p) =>
			p.periods.some((x) => !x.start_time || !x.end_time)
		);
		if (allEmpty) return setFormError(t("aux.messages.select_day"));
		if (incomplete)
			return setFormError(
				"Existen periodos incompletos. Por favor completa o elimina los pares vacíos."
			);

		// select
		if (!tipoContrato) {
			setErrorSelect(true);
			return setFormError("Seleccione un tipo de contrato.");
		}

		// payload
		const payload = {
			id: contractData.id,
			config_site_id: site_id,
			id_tipo_contrato: Number(tipoContrato),
			nombre: values[t("lab_tool.contract.labels.nombre")],
			kronos_name: values[t("lab_tool.contract.labels.kronos_name")],
			productividad: values[t("lab_tool.contract.labels.productividad")],
			rotacion: values[t("lab_tool.contract.labels.rotacion")],
			ausentismo: values[t("lab_tool.contract.labels.ausentismo")],
			cotsup: values[t("lab_tool.contract.labels.cotsup")],
			cotinf: values[t("lab_tool.contract.labels.cotinf")],
			dias_lab_maximos: values[t("lab_tool.contract.labels.dias_lab_maximos")],
			dias_lab_minimos: values[t("lab_tool.contract.labels.dias_lab_minimos")],
			contrato: values[t("lab_tool.contract.labels.contrato")],
			despido: values[t("lab_tool.contract.labels.despido")],
			salario: values[t("lab_tool.contract.labels.salario")],
			zcero: values[t("lab_tool.contract.labels.zcero")],
			hora_contrato: values[t("lab_tool.contract.labels.hora_contrato")],
			contrato_extra: values[t("lab_tool.contract.labels.contrato_extra")],
			meses_contrato:
				values[t("lab_tool.contract.labels.contrato_extra")] === "true"
					? values[t("lab_tool.contract.labels.meses_contrato")]
					: "0",
			contracts_per_day: schedule.days,
			contracts_per_periods: schedule.periods,
		};
		onClose();
		Swal.fire({
			title: t("aux.messages.sure"),
			text: t("aux.messages.not_revert"),
			icon: "warning",
			showCancelButton: true,
			confirmButtonText: t("aux.buttons.yes"),
			cancelButtonText: t("aux.buttons.no"),
		}).then((res) => {
			if (!res.isConfirmed) return;
			actualizarDato("/contracts/update", payload)
				.then(() => {
					auditPost(moduleId, site_id);
					onCreated();
					Swal.fire({
						icon: "success",
						title: t("aux.messages.alert.success"),
						text: t("aux.messages.alert.text_alert.update"),
					});
					onClose();
				})
				.catch(() => {
					Swal.fire({
						icon: "error",
						title: t("aux.messages.alert.error"),
						text: t("aux.messages.alert.text_alert.error_update"),
					});
				});
		});
	};

	if (loading) {
		return (
			<Modal
				open={open}
				onClose={onClose}
				sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
			>
				<CircularProgress />
			</Modal>
		);
	}

	return (
		<Modal
			open={open}
			onClose={onClose}
			sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
		>
			<Box
				sx={{
					width: { xs: "95%", sm: "80%", md: "60%" },
					maxHeight: "90vh",
					bgcolor: "background.paper",
					p: 2,
					overflowY: "auto",
				}}
			>
				{paso === 1 ? (
					<>
						<Typography variant="h6" gutterBottom>
							{t("aux.step")} 1 {t("aux.of")} 2 – {t("config.config_general")}
						</Typography>

						<FormControl fullWidth required error={errorSelect} sx={{ mb: 2 }}>
							<InputLabel>{t("aux.type_contract")}</InputLabel>
							<Select
								value={tipoContrato || ""}
								displayEmpty
								label={t("aux.type_contract")}
								onChange={(e) => {
									setTipoContrato(e.target.value);
									e.target.value && setErrorSelect(false);
								}}
							>
								<MenuItem value="" disabled>
									<em>{t("aux.select_contract_placeholder")}</em>
								</MenuItem>
								{tipoContratos.map((tc) => (
									<MenuItem key={tc.id} value={tc.id.toString()}>
										{tc.nombre}
									</MenuItem>
								))}
							</Select>
						</FormControl>

						<FormConfig
							labels={Object.keys(renameData)}
							valorDefault={values}
							handleFieldChange={handleFieldChange}
							tooltips={tooltips}
							errors={errors}
							fieldConfigs={fieldConfigs}
						/>

						{formError && (
							<Alert severity="error" sx={{ mt: 2 }}>
								{formError}
							</Alert>
						)}

						<Button fullWidth variant="contained" onClick={handleNextStep} sx={{ mt: 2 }}>
							{t("aux.buttons.continue")}
						</Button>
					</>
				) : (
					<>
						<Box sx={{ width: "100%", p: 2 }}>
							<Typography variant="h6" gutterBottom>
								{t("aux.step")} 2 {t("aux.of")} 2 – {t("config.config_day_period")}
							</Typography>

							<GridEdit
								idContract={contractData.id}
								dataDays={schedule.days}
								dataPeriods={schedule.periods}
								onDataUpdate={handleScheduleChange}
							/>

							{formError && (
								<Alert severity="error" sx={{ mt: 2 }}>
									{formError}
								</Alert>
							)}

							<Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
								<Button onClick={handleBack}>{t("aux.buttons.back")}</Button>
								<Button variant="contained" onClick={handleSave}>
									{t("aux.buttons.update")}
								</Button>
							</Box>
						</Box>
					</>
				)}
			</Box>
		</Modal>
	);
}
